import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Stack } from '@astryxdesign/core/Stack'
import { Center } from '@astryxdesign/core/Center'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { IconButton } from '@astryxdesign/core/IconButton'
import { Icon } from '@astryxdesign/core/Icon'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { StatusDot } from '@astryxdesign/core/StatusDot'
import { Tooltip } from '@astryxdesign/core/Tooltip'
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from '@astryxdesign/core/Table'
import { readCourtMonth } from './consoleApi'
import {
    OCCUPANCY_DOTS,
    WEEKDAYS,
    addMonths,
    failureText,
    monthTitle,
    monthWeeks,
    normalizeMonth,
    occupancyLevel,
    occupancyMinutesText,
    percentText,
    todayParts,
} from './consoleText'

// The month view: how full each day is, for the whole club, as one dot per
// day - grey when nothing is taken, then green, amber and red as the day
// fills, measured in minutes held over minutes open (lunar-falcon-2), with
// the lost minutes in the tooltip. A day with nothing to count (closed, or no
// court) has no dot.
// Clicking a day opens it in the day view.
//
// The calendar is a Table: seven columns, one row per week, Monday first.
const cellStyles = {
    day: { cursor: 'pointer', height: 'var(--spacing-12)' },
    today: { cursor: 'pointer', height: 'var(--spacing-12)', backgroundColor: 'var(--color-accent-muted)' },
    blank: { height: 'var(--spacing-12)', backgroundColor: 'var(--color-background-muted)' },
}

// Seven equal columns, whatever each day holds.
const columnStyle = { width: 'calc(100% / 7)' }

const LEGEND = [
    { level: 'empty', text: 'Vide' },
    { level: 'low', text: 'Moins de 50\u00a0%' },
    { level: 'medium', text: 'De 50 à 79\u00a0%' },
    { level: 'high', text: '80\u00a0% et plus' },
]

export default function CourtMonth({ clubId, month, onMonthChange, onOpenDay, onSessionLost }) {
    const [load, setLoad] = useState({ status: 'loading', days: [] })
    const latest = useRef(0)

    const reload = useCallback(async () => {
        const request = ++latest.current
        setLoad({ status: 'loading', days: [] })
        let answer
        try {
            answer = await readCourtMonth(clubId, month)
        } catch (e) {
            answer = { status: 0 }
        }
        if (request !== latest.current) return
        if (answer.status === 200 && answer.json) {
            setLoad({ status: 'ready', days: normalizeMonth(answer.json) })
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            setLoad({ status: 'error', days: [], title: failureText(answer.status) })
        }
    }, [clubId, month, onSessionLost])

    useEffect(() => {
        reload()
    }, [reload])

    const today = todayParts()
    const isThisMonth = today.year === month.year && today.month === month.month
    const byDay = new Map(load.days.map(day => [day.day, day]))

    const navigation = (
        <Stack direction="horizontal" gap={2} vAlign="center" wrap="wrap">
            <IconButton
                label="Mois précédent"
                icon={<Icon icon="chevronLeft" />}
                onClick={() => onMonthChange(addMonths(month, -1))}
            />
            <Button
                label="Ce mois-ci"
                isDisabled={isThisMonth}
                onClick={() => onMonthChange({ year: today.year, month: today.month })}
            />
            <IconButton
                label="Mois suivant"
                icon={<Icon icon="chevronRight" />}
                onClick={() => onMonthChange(addMonths(month, 1))}
            />
            <Heading level={2}>{capitalize(monthTitle(month))}</Heading>
        </Stack>
    )

    let body
    if (load.status === 'loading') {
        body = (
            <Center axis="both" padding={10}>
                <Spinner label="Chargement du mois" />
            </Center>
        )
    } else if (load.status === 'error') {
        body = (
            <Stack gap={3} hAlign="start">
                <Banner status="error" title={load.title} />
                <Button label="Réessayer" variant="secondary" onClick={reload} />
            </Stack>
        )
    } else {
        // Not wrapped in a Stack of its own: a table first in its container
        // reaches up into the container's padding, and here that would put
        // it under the month's title.
        body = (
            <>
                <Table density="compact" dividers="grid" verticalAlign="top">
                    <TableHeader>
                        <TableRow>
                            {WEEKDAYS.map(name => (
                                <TableHeaderCell key={name} style={columnStyle}>{capitalize(name)}</TableHeaderCell>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {monthWeeks(month).map((week, row) => (
                            <TableRow key={row}>
                                {week.map((day, column) => day
                                    ? (
                                        <DayCell
                                            key={day}
                                            day={day}
                                            occupancy={byDay.get(day)}
                                            isToday={isThisMonth && day === today.day}
                                            onOpen={() => onOpenDay({ ...month, day })}
                                        />
                                    )
                                    // A blank cell before the 1st or after the last
                                    // day: its place in the week is its identity.
                                    : <TableCell key={`blank-${row}-${column}`} style={cellStyles.blank} aria-hidden />)}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Legend />
            </>
        )
    }

    return (
        <Stack gap={4}>
            {navigation}
            {body}
        </Stack>
    )
}

CourtMonth.propTypes = {
    clubId: PropTypes.string.isRequired,
    month: PropTypes.shape({
        year: PropTypes.number.isRequired,
        month: PropTypes.number.isRequired,
    }).isRequired,
    onMonthChange: PropTypes.func.isRequired,
    onOpenDay: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}

function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

function DayCell({ day, occupancy, isToday, onOpen }) {
    // In minutes (lunar-falcon-2): a 30-minute block is not a 90-minute game.
    const held = occupancy ? occupancy.held_minutes : 0
    const open = occupancy ? occupancy.open_minutes : 0
    const level = occupancyLevel(held, open)
    const summary = occupancyMinutesText(held, open, occupancy ? occupancy.lost_minutes : 0)
    const percent = percentText(held, open)

    return (
        <TableCell
            style={isToday ? cellStyles.today : cellStyles.day}
            role="button"
            tabIndex={0}
            aria-label={`${day} : ${summary}`}
            onClick={onOpen}
            onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onOpen()
                }
            }}
        >
            <Tooltip content={summary} hasHoverIndication={false}>
                <Stack gap={1}>
                    <Text weight={isToday ? 'bold' : 'medium'} color={isToday ? 'accent' : 'primary'} hasTabularNumbers>
                        {day}
                    </Text>
                    <Stack direction="horizontal" gap={1.5} vAlign="center">
                        {level !== 'closed' && <StatusDot variant={OCCUPANCY_DOTS[level]} label={summary} />}
                        <Text size="sm" color="secondary" hasTabularNumbers>{percent}</Text>
                    </Stack>
                </Stack>
            </Tooltip>
        </TableCell>
    )
}

DayCell.propTypes = {
    day: PropTypes.number.isRequired,
    occupancy: PropTypes.shape({
        held_minutes: PropTypes.number,
        open_minutes: PropTypes.number,
        lost_minutes: PropTypes.number,
    }),
    isToday: PropTypes.bool,
    onOpen: PropTypes.func.isRequired,
}

function Legend() {
    return (
        <Stack direction="horizontal" gap={4} vAlign="center" wrap="wrap">
            <Text type="supporting">Temps occupé :</Text>
            {LEGEND.map(entry => (
                <Stack key={entry.level} direction="horizontal" gap={1.5} vAlign="center">
                    <StatusDot variant={OCCUPANCY_DOTS[entry.level]} label={entry.text} />
                    <Text size="sm">{entry.text}</Text>
                </Stack>
            ))}
            <Stack direction="horizontal" gap={1.5} vAlign="center">
                <Text size="sm" color="secondary">—</Text>
                <Text size="sm">Fermé ou sans terrain</Text>
            </Stack>
        </Stack>
    )
}
